import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardStats,
  DateRangeFilter,
  InputSearch,
  Pagination,
  TableData,
  UniversalFilter,
} from '@/components';
import { Column } from '@/types/common';
import {
  COLLECTION_PURPOSE_LABEL,
  CollectionPurpose,
  PaidCollectionItem,
  PaidCollectionsPage,
} from '@/types/rental';
import { getPaidCollections } from '@/services/api/rentals';
import { showErrorToast } from '@/components/toast/globalToast';
import {
  extractApiErrorMessage,
  formatDateToApi,
  toApiDateRange,
} from '@/utils';
import { formatRupiah } from '@/utils/format';
import {
  amountColumn,
  methodColumn,
  mitraColumn,
  paidDateTimeColumn,
  transactionColumn,
  unitColumn,
} from '@/pages/rent-activity/paidCollectionColumns';

/**
 * Every settled payment, filterable — the investigation screen the dashboard's
 * "Pembayaran Hari Ini" tab deliberately is not: that tab is an action queue
 * frozen on today, and bolting a filter panel onto it would leave one screen
 * answering two different questions.
 *
 * Window, filters and paging all live in URL-free component state on purpose:
 * this screen is opened, read and left, and a shareable link to a filtered
 * money view is not something ops has asked for.
 */

interface PaymentFilters extends Record<string, unknown> {
  purpose?: CollectionPurpose[];
}

const RentPaymentsPage: React.FC = () => {
  const navigate = useNavigate();
  // Defaults to today, like the dashboard: the common question is "what landed
  // today", and a first paint over all history would be a full-table scan for
  // a number nobody asked for.
  const today = useMemo(() => formatDateToApi(new Date()), []);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [filters, setFilters] = useState<PaymentFilters>({});
  // Two pieces of state on purpose: `searchInput` is what the box shows, and
  // `search` is what the server was asked. Feeding keystrokes straight into the
  // fetch dependency fired one request per character.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [rows, setRows] = useState<PaidCollectionItem[]>([]);
  const [summary, setSummary] = useState<PaidCollectionsPage['summary'] | null>(
    null
  );
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const purposeConfigs = useMemo(
    () => [
      {
        key: 'purpose',
        title: 'Peruntukan',
        icon: 'ri-price-tag-3-line',
        multiple: true,
        staticOptions: Object.values(CollectionPurpose).map((purpose) => ({
          key: purpose.toLowerCase(),
          label: COLLECTION_PURPOSE_LABEL[purpose],
          value: purpose,
        })),
      },
    ],
    []
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const result = await getPaidCollections({
          dateFrom,
          dateTo,
          purpose: filters.purpose,
          search: search || undefined,
          page,
          size,
        });
        if (cancelled) return;
        setRows(result.items ?? []);
        setSummary(result.summary);
        setTotal(result.pagination?.total ?? 0);
      } catch (error) {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Gagal memuat daftar pembayaran')
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, filters, search, page, size]);

  const columns: Column<PaidCollectionItem>[] = useMemo(
    () => [
      // Full timestamp, not just the hour: this list spans days.
      paidDateTimeColumn,
      mitraColumn,
      unitColumn,
      transactionColumn,
      methodColumn,
      amountColumn,
    ],
    []
  );

  const openRental = useCallback(
    (record: PaidCollectionItem) => {
      if (record.rental?.id) navigate(`/rent-activity/${record.rental.id}`);
    },
    [navigate]
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
          Pembayaran
        </h1>
        <p className="text-[14px] text-gray-600">
          Semua pembayaran yang sudah masuk, bisa difilter per periode dan
          peruntukan.
        </p>
      </div>

      {/* Window totals, not page totals — the header must not move as ops
          pages through the same filtered set. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <CardStats
          title="Total Masuk"
          value={formatRupiah(summary?.totalAmount ?? 0)}
          icon="ri-money-dollar-circle-line"
          color="green"
          size="small"
          loading={isLoading}
          subtitle="dalam periode & filter aktif"
        />
        <CardStats
          title="Jumlah Transaksi"
          value={summary?.count ?? 0}
          icon="ri-receipt-line"
          color="blue"
          size="small"
          loading={isLoading}
        />
        <CardStats
          title="Admin Fee Terkumpul"
          value={formatRupiah(summary?.totalAdminFee ?? 0)}
          icon="ri-percent-line"
          color="purple"
          size="small"
          loading={isLoading}
        />
      </div>

      {/* Susunan yang sama dengan Handovers, Vehicles, Maintenance,
          Repossessions dan Issues: rentang tanggal di kiri, pencarian lalu
          filter di kanan. Sebelumnya ketiganya menumpuk di kanan, jadi ops
          yang pindah antar menu harus mencari-cari letaknya. */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <DateRangeFilter
              initialTimeRange="today"
              onDateRangeChange={(range) => {
                // Widened where the picker cleared a bound: server-side an
                // absent bound defaults to today, so an empty pair would read
                // as today rather than as all time.
                const window = toApiDateRange(range, today);
                setDateFrom(window.startDate);
                setDateTo(window.endDate);
                setPage(1);
              }}
            />
            {/* Di samping pemilihnya, bukan di bawah: 5 halaman acuan memakai
                `flex items-center`, dan menumpuknya memecah baris yang sama. */}
            <p className="text-[13px] text-gray-500">
              Periode bayar (WIB):{' '}
              <span className="font-medium text-gray-700">
                {summary?.startDate || dateFrom} s.d.{' '}
                {summary?.endDate || dateTo}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Cari kode, metode, mitra, plat..."
              value={searchInput}
              onChange={setSearchInput}
              // Debounced by InputSearch, so this is the only path that refetches.
              onSearch={(value: string) => {
                setSearch(value);
                setPage(1);
              }}
              onClear={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
              debounceMs={300}
              size="small"
            />
            <UniversalFilter
              filters={purposeConfigs}
              currentFilters={filters}
              onApply={(applied: PaymentFilters) => {
                setFilters(applied);
                setPage(1);
              }}
              onClear={() => {
                setFilters({});
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <TableData
        data={rows}
        columns={columns}
        size="small"
        className="min-w-full"
        loading={isLoading}
        showHeaderWhenEmpty
        onRowClick={openRental}
        emptyState={
          <p className="py-10 text-center text-[14px] text-gray-500">
            Tidak ada pembayaran pada periode & filter ini.
          </p>
        }
      />

      <Pagination
        currentPage={page}
        totalItems={total}
        itemsPerPage={size}
        onPageChange={setPage}
        onPageSizeChange={(newSize: number) => {
          setSize(newSize);
          setPage(1);
        }}
      />
    </div>
  );
};

export default RentPaymentsPage;
